import { Button } from "@hortis/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { ChevronDown } from "@hortis/ui/icons";
import { RichTextEditor } from "@hortis/ui/rich-text-editor";
import { useRouteContext } from "@tanstack/react-router";
import { useFormContext } from "react-hook-form";
import { TaxonScientificNameAutocomplete } from "src/components/autocomplete/scientific-names/taxon-scientific-name-autocomplete";
import {
  TagMultiPicker,
  TagMultiPickerLabel,
} from "src/components/pickers/tag-picker/tag-picker";
import { mention } from "src/components/rich-text-editor/mention";
import { SettingsSectionHeader } from "src/features/settings/components/settings-section-header";
import { useAccessTokenBasic } from "src/utils/use-access-token";
import { FormTextField } from "../../../../components/text-field/text-field";
import { AuthorshipField } from "./authorship-field";
import { NativeDistributionField } from "./native-distribution-field";
import type { TaxonFields } from "./schema";

interface TaxonDataFieldsProps {
  formNamePrefix?: string;
  interpretationFieldsEnabled?: boolean;
}

export const TaxonDataFields = ({
  formNamePrefix,
  interpretationFieldsEnabled,
}: TaxonDataFieldsProps) => {
  const form = useFormContext<Partial<TaxonFields>>();
  const accessToken = useAccessTokenBasic();
  const { subdomain } = useRouteContext({ from: "__root__" });
  return (
    <>
      <div className="flex flex-col gap-6">
        <TaxonScientificNameAutocomplete
          name="scientificName"
          testId="scientific-name"
        />
        <AuthorshipField />
        <FormTextField
          fullWidth
          name="commonName"
          id="common-name"
          label="Common name"
          testId="common-name"
        />
        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <FormControl>
                <TagMultiPicker
                  data-cy="taxon-tags"
                  placeholder="Add tags..."
                  newTagOption
                  {...field}
                >
                  <Button
                    className="min-h-[44px] justify-between py-2! pl-3.5!"
                    endIcon={ChevronDown}
                  >
                    {field.value == null || field.value.length === 0 ? (
                      <span className="text-grey-500">Add tags...</span>
                    ) : (
                      <TagMultiPickerLabel value={field.value} />
                    )}
                  </Button>
                </TagMultiPicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notesData"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                {accessToken != null && (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Add notes..."
                    data-cy="taxon-notes-input"
                    extensions={[
                      mention({ accessToken, siteSlug: "", subdomain }),
                    ]}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {interpretationFieldsEnabled === true && (
          <>
            <SettingsSectionHeader
              title="Interpretation"
              subtitle="Description and native distribution"
            />
            <FormTextField
              name="interpretationDescription"
              label="Description"
              placeholder="Write a description"
              multiline
              minRows={2}
              maxRows={6}
              testId="interpretation-description"
              fullWidth
            />
            <NativeDistributionField />
          </>
        )}
      </div>
    </>
  );
};
